import type { GetServerSidePropsContext } from 'next';
import type { OrganizationSiteProfile } from 'types/organization-site';

import SitesApi from '../../../services/api/sites-api';
import { setGoodfyndSitesTitle } from '../../../utils/base';
import type { GoodfyndSiteSection } from '../context/goodfynd-site-context/types';
import type { GoodfyndSiteSeo } from '../types';

export const getSlug = (hostname?: string) => {
  let slug = (hostname || window.location.hostname).replace(/^preview\./, '');
  if (slug.includes('.goodfynd.site')) {
    slug = slug.split('.')[0];
  }

  return slug.replace('https://', '').replace('.com', '').split(':')[0];
};

export interface GoodfyndSitesData {
  goodfyndSiteSeo: GoodfyndSiteSeo;
  organization: OrganizationSiteProfile;
  siteUrl?: string;
}

export const getGoodfyndSitesData = async (
  context?: GetServerSidePropsContext
): Promise<GoodfyndSitesData> => {
  const sitesApi = new SitesApi();

  // Get host from server context or client window
  const host =
    context?.req.headers.host ||
    (typeof window !== 'undefined' ? window.location.host : '');

  if (!host) {
    throw new Error('Host not found');
  }

  const slug = getSlug(host);
  const isPreview = isGoodfyndSitesPreview(host);
  const data = await sitesApi.getOrganizationProfileBySubdomain(slug);

  const organization = isPreview
    ? data.preview
    : data.published ?? data.preview;

  if (!organization) {
    throw new Error('No published site found');
  }

  // Get protocol and construct site URL
  let protocol = 'https';
  let siteUrl: string | undefined;

  if (context) {
    // Server-side: get protocol from headers
    const forwardedProto = context.req.headers['x-forwarded-proto'];
    const protocolHeader = Array.isArray(forwardedProto)
      ? forwardedProto[0]
      : forwardedProto;
    protocol = protocolHeader?.split(',')[0] ?? 'https';
    siteUrl = host ? `${protocol}://${host}${context.resolvedUrl}` : undefined;
  } else {
    // Client-side: use current location
    protocol = window.location.protocol.replace(':', '');
    siteUrl = `${protocol}://${host}${window.location.pathname}`;
  }

  const businessName = organization.businessName;
  const title = setGoodfyndSitesTitle(businessName);
  const defaultDescription = 'Powered by Goodfynd';
  const description =
    organization.description || organization.ourStory || defaultDescription;
  const image = organization.heroImageUrl;
  const sameAsLinks = [
    organization.socials.facebookUrl,
    organization.socials.instagramUrl,
    organization.socials.websiteUrl,
    organization.socials.xUrl,
    ...Object.values(organization.socials.otherUrls),
  ].filter(Boolean) as string[];

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: businessName,
    description,
  };

  if (siteUrl) {
    jsonLd.url = siteUrl;
  }

  if (image) {
    jsonLd.image = image;
  }

  if (sameAsLinks.length > 0) {
    jsonLd.sameAs = sameAsLinks;
  }

  const goodfyndSiteSeo: GoodfyndSiteSeo = {
    businessName,
    canonicalUrl: siteUrl,
    description,
    image,
    jsonLd,
    siteUrl,
    title,
  };

  return { organization, siteUrl, goodfyndSiteSeo };
};

export const isGoodfyndSitesHost = (host?: string) => {
  const path = host || global?.window?.location?.host;
  return !path?.includes('goodfynd.com');
};

export const isGoodfyndSitesPreview = (host?: string) => {
  const path = host || global?.window?.location?.host;
  return isGoodfyndSitesHost(path) && path?.includes('preview.');
};

export const getPreviewUrl = (
  context: GetServerSidePropsContext,
  host: string
): string => {
  // Get protocol
  const forwardedProto = context.req.headers['x-forwarded-proto'];
  const protocolHeader = Array.isArray(forwardedProto)
    ? forwardedProto[0]
    : forwardedProto;
  const protocol = protocolHeader?.split(',')[0] ?? 'https';

  // Build preview host
  let previewHost: string;
  const isGoodfyndSiteHost = host.includes('.goodfynd.site');

  if (isGoodfyndSiteHost) {
    // Insert .preview after subdomain: vendor.goodfynd.site -> vendor.preview.goodfynd.site
    const parts = host.split('.');
    previewHost = `${parts[0]}.preview.${parts.slice(1).join('.')}`;
  } else {
    // Add preview. as subdomain: example.com -> preview.example.com
    previewHost = `preview.${host}`;
  }

  // For .goodfynd.site hosts, use resolvedUrl (includes /goodfynd-sites)
  // For custom domains, use req.url (rewrites remove /goodfynd-sites)
  const path = context.req.url || '/';

  return `${protocol}://${previewHost}${path}`;
};

export const scrollToSection = (section: GoodfyndSiteSection) => {
  const element = document.getElementById(section);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};
